import { memo, useState } from "react";
import type { EnvironmentId } from "@cmmd-forge/contracts";
import {
  buildCollapsedProposedPlanPreviewMarkdown,
  buildProposedPlanMarkdownFilename,
  downloadPlanAsTextFile,
  normalizePlanMarkdownForExport,
  proposedPlanTitle,
  stripDisplayedPlanMarkdown,
} from "../../proposedPlan";
import ChatMarkdown from "../ChatMarkdown";
import { EllipsisIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { cn } from "~/lib/utils";
import { Badge } from "../ui/badge";
import { toastManager } from "../ui/toast";
import { captureWebError } from "~/sentry";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import {
  isWorkspaceFileWriteCancelled,
  useWorkspaceFileWrite,
} from "~/hooks/useWorkspaceFileWrite";
import { ProposedPlanSaveDialog } from "./ProposedPlanSaveDialog";

interface ProposedPlanCardProps {
  readonly planMarkdown: string;
  readonly environmentId: EnvironmentId;
  readonly cwd: string | undefined;
  readonly workspaceRoot: string | undefined;
}

interface ProposedPlanActionsProps {
  readonly isCopied: boolean;
  readonly canSave: boolean;
  readonly onCopy: () => void;
  readonly onDownload: () => void;
  readonly onSave: () => void;
}

function ProposedPlanActions({
  isCopied,
  canSave,
  onCopy,
  onDownload,
  onSave,
}: ProposedPlanActionsProps) {
  return (
    <Menu>
      <MenuTrigger render={<Button aria-label="Plan actions" size="icon-xs" variant="outline" />}>
        <EllipsisIcon aria-hidden="true" className="size-4" />
      </MenuTrigger>
      <MenuPopup align="end">
        <MenuItem onClick={onCopy}>{isCopied ? "Copied!" : "Copy to clipboard"}</MenuItem>
        <MenuItem onClick={onDownload}>Download as markdown</MenuItem>
        <MenuItem onClick={onSave} disabled={!canSave}>
          Save to project
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}

interface ProposedPlanBodyProps {
  readonly canCollapse: boolean;
  readonly expanded: boolean;
  readonly collapsedPreview: string | null;
  readonly displayedPlanMarkdown: string;
  readonly cwd: string | undefined;
  readonly onToggleExpanded: () => void;
}

function ProposedPlanBody({
  canCollapse,
  expanded,
  collapsedPreview,
  displayedPlanMarkdown,
  cwd,
  onToggleExpanded,
}: ProposedPlanBodyProps) {
  const isCollapsed = canCollapse && !expanded;
  return (
    <div className="mt-4">
      <div className={cn("relative", isCollapsed ? "max-h-104 overflow-hidden" : undefined)}>
        <ChatMarkdown
          text={isCollapsed ? (collapsedPreview ?? "") : displayedPlanMarkdown}
          cwd={cwd}
          isStreaming={false}
        />
        {isCollapsed ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-linear-to-t from-card/95 via-card/80 to-transparent" />
        ) : null}
      </div>
      {canCollapse ? (
        <div className="mt-4 flex justify-center">
          <Button size="sm" variant="outline" data-scroll-anchor-ignore onClick={onToggleExpanded}>
            {expanded ? "Collapse plan" : "Expand plan"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export const ProposedPlanCard = memo(function ProposedPlanCard({
  planMarkdown,
  environmentId,
  cwd,
  workspaceRoot,
}: ProposedPlanCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [savePath, setSavePath] = useState("");
  const workspaceWrite = useWorkspaceFileWrite(environmentId);
  const { copyToClipboard, isCopied } = useCopyToClipboard({
    onError: (error) => {
      toastManager.add({
        type: "error",
        title: "Could not copy plan",
        description: error instanceof Error ? error.message : "An error occurred while copying.",
      });
    },
  });
  const title = proposedPlanTitle(planMarkdown) ?? "Proposed plan";
  const lineCount = planMarkdown.split("\n").length;
  const canCollapse = planMarkdown.length > 900 || lineCount > 20;
  const displayedPlanMarkdown = stripDisplayedPlanMarkdown(planMarkdown);
  const collapsedPreview = canCollapse
    ? buildCollapsedProposedPlanPreviewMarkdown(planMarkdown, { maxLines: 10 })
    : null;
  const downloadFilename = buildProposedPlanMarkdownFilename(planMarkdown);
  const saveContents = normalizePlanMarkdownForExport(planMarkdown);
  const isSavingToWorkspace = workspaceWrite.state.status === "saving";

  const handleDownload = () => {
    downloadPlanAsTextFile(downloadFilename, saveContents);
  };

  const handleCopyPlan = () => {
    copyToClipboard(saveContents);
  };

  const openSaveDialog = () => {
    if (!workspaceRoot) {
      toastManager.add({
        type: "error",
        title: "Project path is unavailable",
        description: "This thread does not have a project path to save into.",
      });
      return;
    }
    setSavePath((existing) => (existing.length > 0 ? existing : downloadFilename));
    workspaceWrite.reset();
    setIsSaveDialogOpen(true);
  };

  const handleSaveToWorkspace = async () => {
    const relativePath = savePath.trim();
    if (!workspaceRoot) return;
    if (!relativePath) {
      toastManager.add({
        type: "warning",
        title: "Enter a project path",
      });
      return;
    }

    try {
      const result = await workspaceWrite.save({
        cwd: workspaceRoot,
        relativePath,
        contents: saveContents,
      });
      setIsSaveDialogOpen(false);
      toastManager.add({
        type: "success",
        title: "Plan saved to project",
        description: result.relativePath,
      });
    } catch (error) {
      if (isWorkspaceFileWriteCancelled(error)) {
        toastManager.add({
          type: "info",
          title: "Save cancelled",
          description: "The incomplete transfer was discarded.",
        });
        return;
      }
      captureWebError(error, { route: "proposed-plan-save" });
      toastManager.add({
        type: "error",
        title: "Could not save plan",
        description: error instanceof Error ? error.message : "An error occurred while saving.",
      });
    }
  };

  const handleSaveDialogOpenChange = (open: boolean) => {
    if (!isSavingToWorkspace) setIsSaveDialogOpen(open);
  };

  const handleSaveDialogCancel = () => {
    if (isSavingToWorkspace) {
      workspaceWrite.cancel();
      return;
    }
    setIsSaveDialogOpen(false);
  };

  return (
    <div className="rounded-[24px] border border-border/80 bg-card/70 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Badge variant="secondary">Plan</Badge>
          <p className="truncate text-sm font-medium text-foreground">{title}</p>
        </div>
        <ProposedPlanActions
          isCopied={isCopied}
          canSave={Boolean(workspaceRoot) && !isSavingToWorkspace}
          onCopy={handleCopyPlan}
          onDownload={handleDownload}
          onSave={openSaveDialog}
        />
      </div>
      <ProposedPlanBody
        canCollapse={canCollapse}
        expanded={expanded}
        collapsedPreview={collapsedPreview}
        displayedPlanMarkdown={displayedPlanMarkdown}
        cwd={cwd}
        onToggleExpanded={() => setExpanded((value) => !value)}
      />
      <ProposedPlanSaveDialog
        open={isSaveDialogOpen}
        workspaceRoot={workspaceRoot}
        savePath={savePath}
        defaultFilename={downloadFilename}
        transferState={workspaceWrite.state}
        onOpenChange={handleSaveDialogOpenChange}
        onSavePathChange={setSavePath}
        onCancel={handleSaveDialogCancel}
        onSave={handleSaveToWorkspace}
      />
    </div>
  );
});
